/**
 * Cloudflare Workers Authentication API
 * 
 * Workers-based authentication with Telegram integration
 */

const WORKERS_API_URL = import.meta.env.VITE_API_URL || 'https://api.trafomatic.reg2dep.net';

// Константа для ключа хранения выбранной роли (синхронизована с RoleSelectionPage)
const ONBOARDING_ROLE_KEY = "onboardingRole";

export interface WorkersAuthSession {
  access_token: string;
  refresh_token: string;
  user: {
    id: number;
    telegram_id: number;
    username?: string;
    first_name: string;
    last_name?: string;
    language_code?: string;
    photo_url?: string;
  };
  expires_in: number;
  is_new_user: boolean;
  user_role: string | null;
}

export interface WorkersAuthError {
  error: string;
  message?: string;
}

/**
 * Аутентифікація через Telegram initData
 * 
 * CRITICAL FIX: Автоматично читає вибрану роль з sessionStorage
 * та передає її на сервер для нових користувачів
 */
export async function authenticateWithTelegram(
  initData: string,
  turnstileToken?: string
): Promise<WorkersAuthSession> {
  // ✅ CRITICAL FIX: Читаємо роль з sessionStorage
  // Роль зберігається на RoleSelectionPage перед викликом authenticateWithTelegram
  const savedRole = sessionStorage.getItem(ONBOARDING_ROLE_KEY);
  
  console.log('[WorkersAuth] 🔐 Authenticating with Telegram:', {
    hasInitData: !!initData,
    initDataLength: initData?.length,
    savedRole,
    hasTurnstileToken: !!turnstileToken,
    timestamp: new Date().toISOString(),
  });

  console.log('[WorkersAuth] 📦 Request body will contain:', {
    hasInitData: !!initData,
    hasTurnstileToken: !!turnstileToken,
    role: savedRole,
    roleSource: savedRole ? 'sessionStorage' : 'null'
  });

  const response = await fetch(`${WORKERS_API_URL}/api/auth/telegram`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      initData,
      turnstileToken,
      role: savedRole, // ✅ CRITICAL FIX: Передаємо роль на сервер
    }),
  });

  if (!response.ok) {
    const error: WorkersAuthError = await response.json();
    throw new Error(error.message || error.error || 'Authentication failed');
  }

  const data = await response.json();
  
  if (!data.isValid) {
    throw new Error('Invalid Telegram data');
  }

  // Зберігаємо токени в localStorage
  if (data.access_token) {
    localStorage.setItem('workers_access_token', data.access_token);
  }
  if (data.refresh_token) {
    localStorage.setItem('workers_refresh_token', data.refresh_token);
  }
  if (data.user) {
    localStorage.setItem('workers_user', JSON.stringify(data.user));
  }

  // ✅ CRITICAL FIX: DO NOT clear role here! 
  // AuthContext needs to read it AFTER this function returns
  // Role will be cleared in AuthContext after saving to server
  console.log('[WorkersAuth] Authentication successful, role will be cleared by AuthContext');

  return data;
}

/**
 * Отримати поточну сесію з localStorage
 */
export function getStoredSession(): WorkersAuthSession | null {
  try {
    const accessToken = localStorage.getItem('workers_access_token');
    const refreshToken = localStorage.getItem('workers_refresh_token');
    const userStr = localStorage.getItem('workers_user');

    if (!accessToken || !userStr) {
      return null;
    }

    // ✅ CRITICAL FIX: Додано null-check для user після JSON.parse
    const user = JSON.parse(userStr);
    if (!user || typeof user !== 'object') {
      console.warn('[WorkersAuth] Invalid user data in localStorage');
      return null;
    }

    return {
      access_token: accessToken,
      refresh_token: refreshToken || '',
      user,
      expires_in: 3600,
      is_new_user: false,
      user_role: null,
    };
  } catch (error) {
    console.error('[WorkersAuth] Error reading stored session:', error);
    return null;
  }
}

/**
 * Вихід з системи
 */
export function signOut(): void {
  localStorage.removeItem('workers_access_token');
  localStorage.removeItem('workers_refresh_token');
  localStorage.removeItem('workers_user');
  
  // Notify listeners about sign out
  notifySignOut();
  
  console.log('[WorkersAuth] User signed out, session cleared');
}

/**
 * Перевірка чи є активна сесія
 */
export function hasActiveSession(): boolean {
  return !!localStorage.getItem('workers_access_token');
}

/**
 * Отримати access token для API запитів
 */
export function getAccessToken(): string | null {
  return localStorage.getItem('workers_access_token');
}

/**
 * Event-based auth state listener
 * For compatibility with existing auth flow
 */
export type AuthChangeEvent = 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED';
export type AuthChangeCallback = (event: AuthChangeEvent, session: WorkersAuthSession | null) => void;

const authListeners: AuthChangeCallback[] = [];

export function onAuthStateChange(callback: AuthChangeCallback) {
  authListeners.push(callback);

  // Викликаємо callback з поточним станом
  const session = getStoredSession();
  callback(session ? 'SIGNED_IN' : 'SIGNED_OUT', session);

  // Повертаємо unsubscribe функцію
  return {
    subscription: {
      unsubscribe: () => {
        const index = authListeners.indexOf(callback);
        if (index > -1) {
          authListeners.splice(index, 1);
        }
      },
    },
  };
}

/**
 * Повідомити всіх слухачів про зміну auth стану
 */
export function notifyAuthChange(event: AuthChangeEvent, session: WorkersAuthSession | null): void {
  authListeners.forEach(callback => {
    try {
      callback(event, session);
    } catch (error) {
      console.error('[WorkersAuth] Error in auth listener:', error);
    }
  });
}

/**
 * Helper для виклику після успішної аутентифікації
 */
export function notifySignIn(session: WorkersAuthSession): void {
  notifyAuthChange('SIGNED_IN', session);
}

/**
 * Helper для виклику після виходу
 */
export function notifySignOut(): void {
  notifyAuthChange('SIGNED_OUT', null);
}

// ✅ CRITICAL FIX: Listen for 401 signout events from workers-api
if (typeof window !== 'undefined') {
  window.addEventListener('workers-auth-signout', () => {
    console.log('[WorkersAuth] 🔄 Received signout event from API (401), triggering re-authentication');
    signOut();
  });
}
